import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TDropdownListItem } from '@cynteka/ui-kit';

import { ChatSessionItem } from '../../../types/chat-session.types';
import { ChatPageScrollEdge, findVerticalScrollParent, getScrollTopForEdge } from '../../../utils/scroll-edge';
import { ChatSessionItemComponent, ChatSessionItemType } from '../chat-session-item/chat-session-item.component';

@Component({
    selector: 'chat-session-list',
    standalone: true,
    imports: [CommonModule, ChatSessionItemComponent],
    templateUrl: './chat-session-list.component.html',
    styleUrls: ['./chat-session-list.component.scss'],
})
export class ChatSessionListComponent implements OnChanges, AfterViewChecked {
    @Input() public sessions: ChatSessionItem[] = [];
    @Input() public type: ChatSessionItemType = 'user';
    @Input() public showAvatar = true;
    @Input() public showOnlineIndicator = true;
    @Input() public showUnreadIndicator = true;
    @Input() public createLabel: string | null = null;
    @Input() public menuItems: ((session: ChatSessionItem) => TDropdownListItem[]) | null = null;
    @Input() public hasMoreBefore = false;
    @Input() public hasMoreAfter = false;
    @Input() public isLoadingBefore = false;
    @Input() public isLoadingAfter = false;
    @Output() public sessionSelected = new EventEmitter<string>();
    @Output() public sessionDeleted = new EventEmitter<string>();
    @Output() public createClicked = new EventEmitter<void>();
    @Output() public loadMoreBefore = new EventEmitter<void>();
    @Output() public loadMoreAfter = new EventEmitter<void>();

    private pageScrollEdge: ChatPageScrollEdge | null = null;
    private applyPageScroll = false;
    private lastFirstSessionId: string | null | undefined = undefined;

    public constructor(private readonly host: ElementRef<HTMLElement>) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes['sessions']) {
            return;
        }

        const firstId = this.sessions[0]?.id ?? null;
        const isPageReplace = this.lastFirstSessionId !== undefined
            && this.lastFirstSessionId !== null
            && firstId !== this.lastFirstSessionId;

        this.lastFirstSessionId = firstId;

        if (isPageReplace && this.pageScrollEdge) {
            this.applyPageScroll = true;
        }
    }

    public ngAfterViewChecked(): void {
        if (!this.applyPageScroll || !this.pageScrollEdge) {
            return;
        }

        const edge = this.pageScrollEdge;
        this.applyPageScroll = false;
        this.pageScrollEdge = null;
        requestAnimationFrame(() => this.scrollToEdge(edge));
    }

    public onLoadMoreBefore(): void {
        this.pageScrollEdge = 'start';
        this.loadMoreBefore.emit();
    }

    public onLoadMoreAfter(): void {
        this.pageScrollEdge = 'end';
        this.loadMoreAfter.emit();
    }

    private scrollToEdge(edge: ChatPageScrollEdge): void {
        const container = findVerticalScrollParent(this.host.nativeElement) ?? this.host.nativeElement;
        const nextScrollTop = getScrollTopForEdge(container, edge);

        if (Math.abs(container.scrollTop - nextScrollTop) > 1) {
            container.scrollTop = nextScrollTop;
        }
    }
}
